import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import Image from 'next/image'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import styled from '@emotion/styled'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'

const collapsedLineCount = 10
const imageGap = 24
const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect

const CompanyIntroductionImageSlide = styled(SwiperSlide)`
  ${({ theme }) => `
    && {
      width: 80vw;
    }

    ${theme.breakpoints.up('lg')} {
      && {
        width: calc((100% - ${imageGap * 2}px) / 3);
      }
    }
  `}
`

const CompanyIntroduction = ({ introduction }) => {
  const [expanded, setExpanded] = useState(false)
  const [isOverflow, setIsOverflow] = useState(false)
  const textRef = useRef(null)
  const content = introduction?.content
  const images =
    introduction?.images?.filter((item) => item?.image?.url).slice(0, 3) || []

  useIsomorphicLayoutEffect(() => {
    const textElement = textRef.current
    if (!content?.trim() || !textElement) return

    const updateOverflow = () => {
      const style = window.getComputedStyle(textElement)
      const fontSize = parseFloat(style.fontSize) || 16
      const lineHeight = parseFloat(style.lineHeight) || fontSize * 1.5
      const collapsedHeight = lineHeight * collapsedLineCount
      setIsOverflow(textElement.scrollHeight > collapsedHeight + 1)
    }

    updateOverflow()
    if (typeof window.ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateOverflow)
      return () => window.removeEventListener('resize', updateOverflow)
    }

    const resizeObserver = new window.ResizeObserver(updateOverflow)
    resizeObserver.observe(textElement)
    return () => resizeObserver.disconnect()
  }, [content])

  if (!content?.trim()) return null

  return (
    <Box component="section" mb={{ xs: 12, md: 18 }}>
      <Typography component="h2" variant="h5" mb={6}>
        公司介紹
      </Typography>

      <Typography
        ref={textRef}
        component="div"
        variant="paraBodyMedium"
        color="text.minus2"
        display="-webkit-box"
        overflow="hidden"
        whiteSpace="pre-wrap"
        sx={{
          WebkitLineClamp: expanded ? 'unset' : collapsedLineCount,
          WebkitBoxOrient: 'vertical',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}
      >
        {content}
      </Typography>

      {(isOverflow || expanded) && (
        <Button
          variant="text"
          aria-label={expanded ? '收合' : '查看完整資訊'}
          onClick={() => setExpanded((value) => !value)}
          endIcon={
            expanded ? (
              <ExpandLessIcon sx={{ width: 24, height: 24 }} />
            ) : (
              <ExpandMoreIcon sx={{ width: 24, height: 24 }} />
            )
          }
          sx={{ mt: 4, color: 'primary.mainPlus2' }}
        >
          {expanded ? '收合' : '查看完整資訊'}
        </Button>
      )}

      {!!images.length && (
        <Box mt={6} overflow="hidden">
          <Swiper slidesPerView="auto" spaceBetween={imageGap}>
            {images.map((item) => (
              <CompanyIntroductionImageSlide key={item.image._id}>
                <Box
                  position="relative"
                  borderRadius="16px"
                  overflow="hidden"
                  sx={{ aspectRatio: '3 / 2' }}
                >
                  <Image
                    src={item.image.url}
                    alt={item.alt || ''}
                    fill
                    sizes="(max-width: 991px) 80vw, (max-width: 1600px) 33vw, 480px"
                    style={{ objectFit: 'cover' }}
                  />
                </Box>
              </CompanyIntroductionImageSlide>
            ))}
          </Swiper>
        </Box>
      )}
    </Box>
  )
}

export default CompanyIntroduction
